import { useMemo, useState } from 'react';
import type { TimetableDay } from '@noorview/core';
import { exportTimetableCsv, indexTimetable, normaliseTime, pad2, ymd } from '@noorview/core';
import { attempt, backend, toast, useApp } from '../data/store';
import { clearedMonth, forgetClearedMonth, keepClearedMonth } from '../data/monthBackup';
import { Icon } from '../ui/icons';
import { IconButton, confirm } from '../ui/controls';
import { openSheet } from '../app/ui';
import { LockedArea } from '../app/lock';
import { download } from '../sheets/Csv';

const COLS: Array<[keyof TimetableDay, string, boolean]> = [
  ['sunrise', 'Sunrise', false], ['fajr', 'Fajr', false], ['fajrJamaah', 'Jamaat', true], ['dhuhr', 'Zuhr', false], ['dhuhrJamaah', 'Jamaat', true],
  ['asr', 'Asr', false], ['asrJamaah', 'Jamaat', true], ['maghrib', 'Maghrib', false], ['maghribJamaah', 'Jamaat', true], ['isha', 'Isha', false], ['ishaJamaah', 'Jamaat', true],
  ['jumuah1', 'Jumuah 1', true], ['jumuah2', 'Jumuah 2', true], ['sehri', 'Sehri ends', false], ['tarawih', 'Tarawih', true],
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function TimetablePage() {
  const ws = useApp((s) => s.ws)!;
  const tz = ws.masjid.timezone;
  const today = ymd(new Date(), tz);
  const [ym, setYm] = useState(() => ({ y: +today.slice(0, 4), m: +today.slice(5, 7) }));
  const [edits, setEdits] = useState<Record<string, Partial<TimetableDay>>>({});
  const [busy, setBusy] = useState(false);
  const index = useMemo(() => indexTimetable(ws.timetable), [ws.timetable]);
  const days = useMemo(() => {
    const n = new Date(Date.UTC(ym.y, ym.m, 0)).getUTCDate();
    return Array.from({ length: n }, (_, i) => `${ym.y}-${pad2(ym.m)}-${pad2(i + 1)}`);
  }, [ym]);
  const dirty = Object.keys(edits).length > 0;
  const bad = Object.values(edits).some((e) => Object.values(e).some((v) => v !== '' && v != null && !normaliseTime(String(v))));
  const shift = (d: number) => { if (dirty) return; setYm(({ y, m }) => { const t = m + d; return { y: y + Math.floor((t - 1) / 12), m: ((t - 1 + 120) % 12) + 1 }; }); };

  const save = async () => {
    const out: TimetableDay[] = [];
    for (const [date, e] of Object.entries(edits)) {
      const row: TimetableDay = { ...(index.get(date) ?? { date }) };
      for (const [k, v] of Object.entries(e)) {
        const t = v ? normaliseTime(String(v)) : null;
        if (t) (row as unknown as Record<string, string>)[k] = t; else delete (row as unknown as Record<string, string>)[k];
      }
      out.push(row);
    }
    setBusy(true);
    const ok = await attempt(() => backend.setTimetable(out.map((r) => ({ ...r })), 'merge'), 'Timetable saved for every screen');
    setBusy(false);
    if (ok !== undefined) setEdits({});
  };
  // Clear month asks first, and keeps the month's times on this device for 30 days so it can be restored.
  const store = typeof localStorage !== 'undefined' ? localStorage : null;
  const monthHasTimes = days.some((d) => index.has(d));
  const backup = store && !monthHasTimes ? clearedMonth(store, ws.masjid.id, ym.y, ym.m) : null;
  const restoreMonth = async (y: number, m: number) => {
    const saved = store ? clearedMonth(store, ws.masjid.id, y, m) : null;
    if (!saved) return;
    const ok = await attempt(() => backend.setTimetable(saved, 'days'), `${MONTHS[m - 1]} restored`);
    if (ok !== undefined && store) forgetClearedMonth(store, ws.masjid.id, y, m);
  };
  const clearMonth = async () => {
    const have = days.filter((d) => index.has(d));
    if (!have.length) return;
    const { y, m } = ym;
    if (!(await confirm(`Are you sure you want to clear ${MONTHS[m - 1]}?`, { body: `This removes every time for ${MONTHS[m - 1]} ${y}. Screens show no times for these days until they’re restored, uploaded or typed again. You can restore the month from this page for 30 days.`, ok: 'Clear month', danger: true }))) return;
    if (store) keepClearedMonth(store, ws.masjid.id, y, m, ws.timetable.filter((r) => have.includes(r.date)));
    const ok = await attempt(() => backend.setTimetable(ws.timetable.filter((r) => !have.includes(r.date)), 'replace'));
    if (ok !== undefined) toast(`${MONTHS[m - 1]} cleared`, { action: { label: 'Undo', run: () => void restoreMonth(y, m) }, ms: 10000 });
  };

  return (
    <div className="page">
      <header className="page-head">
        <div><h1>Timetable</h1><p>Every time your screens show comes from here. Upload your CSV, or type over any time.</p></div>
        <span className="spacer" />
        <button type="button" className="btn secondary" onClick={() => openSheet({ name: 'csv' })}><Icon name="upload" size={18} />Upload CSV</button>
        <button type="button" className="btn secondary" onClick={() => download(`noorview-timetable-${ym.y}-${pad2(ym.m)}.csv`, exportTimetableCsv(days.map((d) => index.get(d) ?? { date: d })))}><Icon name="download" size={18} />Export month</button>
      </header>
      <LockedArea what="the timetable">
      <section className="card glass">
        <div className="card-head tt-bar">
          <IconButton icon="back" label="Previous month" size="sm" onClick={() => shift(-1)} disabled={dirty} />
          <h2 style={{ minWidth: 170, textAlign: 'center' }}>{MONTHS[ym.m - 1]} {ym.y}</h2>
          <IconButton icon="chevron" label="Next month" size="sm" onClick={() => shift(1)} disabled={dirty} />
          <span className="spacer" />
          {dirty ? (<>
            <span className="hint">{Object.keys(edits).length} day{Object.keys(edits).length === 1 ? '' : 's'} changed</span>
            <button type="button" className="btn sm plain" onClick={() => setEdits({})}>Discard</button>
            <button type="button" className="btn sm primary" disabled={busy || bad} onClick={save}>{bad ? 'Fix the red times' : busy ? 'Saving…' : 'Save changes'}</button>
          </>) : backup ? <button type="button" className="btn sm primary" onClick={() => void restoreMonth(ym.y, ym.m)}><Icon name="refresh" size={16} />Restore {MONTHS[ym.m - 1]}</button>
            : <button type="button" className="btn sm plain" disabled={!monthHasTimes} onClick={clearMonth}>Clear month</button>}
        </div>
        <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 300px)' }}>
          <table className="tt">
            <thead><tr><th>Date</th>{COLS.map(([k, l]) => <th key={k}>{l}</th>)}</tr></thead>
            <tbody>
              {days.map((d) => {
                const row = index.get(d);
                const e = edits[d] ?? {};
                const fri = new Date(`${d}T12:00:00Z`).getUTCDay() === 5;
                const wd = new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
                return (
                  <tr key={d} className={d === today ? 'today' : ''}>
                    <th>{wd} {+d.slice(8)}</th>
                    {COLS.map(([k]) => {
                      const typed = k in e ? e[k] : row?.[k];
                      const v = typed ?? '';
                      const isBad = v !== '' && !normaliseTime(String(v));
                      const na = (k === 'jumuah1' || k === 'jumuah2') && !fri;
                      return (
                        <td key={k} className={`${typed ? '' : 'calc'}${isBad ? ' bad' : ''}`}>
                          <input value={String(v)} placeholder={na ? '' : '—'} disabled={na} aria-label={`${d} ${k}`} inputMode="numeric" maxLength={8}
                            onChange={(ev) => setEdits((all) => ({ ...all, [d]: { ...all[d], [k]: ev.target.value } }))} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      </LockedArea>
    </div>
  );
}
